import { Component, OnInit, AfterViewInit } from '@angular/core';
import {
  Schema,
  SchemaMetadataModel,
  DynamicSchemaFacade,
  DynamicSchemaFactory,
  SchemaMetadataFacade,
  DynamicContentSchemaFactory,
  DynamicContentModel,
  DynamicContentSchemaFacade,
  FilterPagination,
  AuthenticationService
} from '../../services';

import { PageTitleService } from '../../services/general/page-title.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BaseComponent } from '../../base/base-component.component';
import { AppSchemaCategory } from '../../models/app-token';
import { flatMap, map, mergeMap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { FieldReferenceContent } from '../../shared/field/tools/field-reference-content.service';
import { ESchemaContentStatus } from '../../services/akita/base/enum';

@Component({
  selector: 'app-contents',
  templateUrl: './contents.component.html',
  styleUrls: ['./contents.component.css']
})
export class ContentsComponent extends BaseComponent implements OnInit, AfterViewInit {

  schemaSelectedName = '';
  schemaSelectedLabel = '';
  contentSelectedCode = '';

  schemaDefinition: Schema;
  schemaMetadata: SchemaMetadataModel;
  listableSchemas: Schema[];
  listableSchemasMetadata: SchemaMetadataModel[];

  currentSchemaFacade: DynamicSchemaFacade;
  currentContentsFacade: DynamicContentSchemaFacade;

  selectedSchemaContents: DynamicContentModel[];
  selectedContent: DynamicContentModel;

  currentPageSchemas: Schema[];

  referenceFieldValues: any;
  referenceFieldValues$: Observable<any>;
  orderBySwitch: boolean;

  paginationSize: number = 14;
  totalItems: number;
  currentPage: number = 1;
  isAlphaOrder: boolean = false;

  userIsOwner: boolean;
  userIsDev: boolean;
  userIsEditor: boolean;
  userIsReadWrite: boolean;
  userIsReader: boolean;


  constructor(
    private readonly dynamicSchemaFacto: DynamicSchemaFactory,
    private readonly dynamicContentSchemaFactory: DynamicContentSchemaFactory,
    private readonly pageTitleService: PageTitleService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly metadataFacade: SchemaMetadataFacade,
    private readonly referenceContentService: FieldReferenceContent,
    private authenticationService: AuthenticationService,
    translateService: TranslateService) {
    super(translateService);
    this.setTranslateContext('ContentsComponent');
  }

  ngOnInit() {

    this.userIsOwner = this.authenticationService.userHasRole("Owner");
    this.userIsDev = this.authenticationService.userHasRole("Developer");
    this.userIsEditor = this.authenticationService.userHasRole("Editor");
    this.userIsReadWrite = this.authenticationService.userHasRole("ReadWrite");
    this.userIsReader = this.authenticationService.userHasRole("Reader");

    if (!this.userIsOwner && !this.userIsDev && !this.userIsEditor && !this.userIsReadWrite && !this.userIsReader) {
      this.router.navigateByUrl('/');
      return;
    }

    const sub1 = this.dynamicSchemaFacto
      .getSchemasMetadataByCategory(AppSchemaCategory.SCHEMA_AS_A_PAGE).pipe(flatMap(defaults => {
        return this.dynamicSchemaFacto
          .getSchemasMetadataByCategory(AppSchemaCategory.CONTENT_SCHEMAS).pipe(map(contents => {
            return defaults.concat(contents).sort(this.sortByLabel);
          }));
      }))
      .subscribe(result => {
        this.listableSchemas = result.filter(meta => meta.name.length <= 40);
        const start: number = (this.currentPage - 1) * this.paginationSize;
        const end = start + this.paginationSize;
        this.totalItems = this.listableSchemas.length;
        this.currentPageSchemas = this.listableSchemas.slice(start, end);
      });

    this.subscriptions.push(sub1);

    this.updateObservable();

    const sub = this.tc('TITLE').subscribe(title => {
      this.pageTitleService.setTitle(title as string);
      this.pageTitleService.key = 'CONTENTS.TITLE';
    });
    this.subscriptions.push(sub);
    this.orderBySwitch = false;
  }

  ngAfterViewInit(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  updateObservable() {
    this.schemaSelectedName = this.route.snapshot.paramMap.get('name');
    this.contentSelectedCode = this.route.snapshot.paramMap.get('code');

    if (this.schemaSelectedName && this.schemaSelectedName.length > 0) {
      this.currentSchemaFacade = this.dynamicSchemaFacto.getDynamicSchemaFacade(this.schemaSelectedName);


      this.referenceFieldValues$ = this.currentSchemaFacade
        .getSchemaDefinition()
        .pipe(mergeMap(result => {
          this.schemaDefinition = result;
          this.schemaSelectedLabel = this.schemaDefinition["properties"]["label"];
          return this.referenceContentService.generateContentList(result);
        }));

      var sub = this.referenceFieldValues$.subscribe();
      this.subscriptions.push(sub);

      const sub1 = this.metadataFacade
        .getByCode(this.schemaSelectedName)
        .subscribe(result => this.schemaMetadata = result);

      this.subscriptions.push(sub1);

      this.currentContentsFacade = this.dynamicContentSchemaFactory
        .getDynamicContentSchemaFacade(this.schemaSelectedName);

      this.filterValue = this.route.snapshot.paramMap.get('filter');
      this.currentPageNumber = Number(this.route.snapshot.paramMap.get('page'));

      if (this.filterValue === null) {
        this.filterValue = '';
      }

      const filterObject = {
        or:
          [
            {
              path: 'data.code.iv',
              op: 'contains',
              value: this.filterValue
            }
          ]
      }

      const filter = new FilterPagination();

      filter.from = (this.currentPageNumber - 1) * this.paginateSize;
      filter.to = filter.from + this.paginateSize;
      filter.orderBy = 'data.index.iv ascending,data.code.iv ascending';
      filter.filterObject = this.getSearchObject(this.filterValue);

      this.currentContentsFacade.getFilteredPagination(filter, ESchemaContentStatus.All, filterObject)
        .subscribe(contents => {
          this.selectedSchemaContents = contents;
          this.currentContentsFacade.fullCount().subscribe(c => {
            this.contentCount = c;
          });
        });

      if (this.contentSelectedCode && this.contentSelectedCode.length > 0) {

        this.currentContentsFacade.getByCode(this.contentSelectedCode, true).subscribe(c => {
          this.selectedContent = c;
        });
      }

    } else {
      this.schemaDefinition = null;
      this.schemaMetadata = null;
    }
  }

  getSearchObject(filter): object {
    const oSearch = {
      or:
        [
          {
            path: 'data.code.iv',
            op: 'contains',
            value: filter
          }
        ]
    }
    return oSearch;
  }

  updatePaginateSize(qty: number) {
    this.paginateSize = qty;
    this.updateObservable();
  }

  unselectSchema() {
    this.schemaSelectedName = '';
    this.selectedSchemaContents = null;
  }

  getPageData($event) {
    this.currentPageNumber = $event.page;
    this.search(null, true);
  }

  getSearchFilterContent($event) {
    this.currentContentsFacade.getFilteredPagination($event, ESchemaContentStatus.All, $event.filterObject)
      .subscribe(contents => {
        this.selectedSchemaContents = contents;
        this.currentContentsFacade.fullCount().subscribe(c => {
          this.contentCount = c;
        });
      });
  }

  setOrderBySwitch(bySwitch: boolean) {
    this.orderBySwitch = bySwitch;
  }

  pageChanged(event: any) {

    this.currentPage = event.page;

    const start: number = (this.currentPage - 1) * this.paginationSize;
    const end = start + this.paginationSize;

    this.currentPageSchemas = this.listableSchemas.slice(start, end);
  }

  setAlphaOrder(isAlpha: boolean) {
    this.isAlphaOrder = isAlpha;
  }

  redirectToSelectedSchema() {
    this.router.navigateByUrl(`/schemas/${this.schemaSelectedName}`);
  }

}
